import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, usePlayer } from '../contexts/AppContext';
import { Button } from '@/components/ui/button';
import { 
  Play, Pause, ChevronLeft, ChevronRight, Plus, Shuffle, Heart, MoreHorizontal, Share2, Flag
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ReportModal } from '../components/ReportModal';
import { resolveThumbnailUrl, resolvePlaybackUrl } from '../utils/hlsPlayer';
import { CommentSection } from '../components/CommentSection';
import { shareVideo } from '../utils/share';
import { applyVideoSeo } from '../utils/videoSeo';
import { AIDisclosureBadges } from '../components/AIDisclosure';
import { useLikeAuthGate } from '../components/AuthPromptModal';
import { FoundingCreatorBadge } from '../components/FoundingCreatorBadge';
import { FounderBadge } from '../components/FounderBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const GENRE_THUMBS = {
  'Hip-Hop / Rap': "https://images.unsplash.com/photo-1771310961652-1af9711686ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxoaXAlMjBob3AlMjByYXBwZXIlMjBzdHVkaW8lMjBkYXJrfGVufDB8fHx8MTc3NTE4Njc1MHww&ixlib=rb-4.1.0&q=85",
  'Jazz': "https://images.pexels.com/photos/359995/pexels-photo-359995.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  'Rock': "https://images.unsplash.com/photo-1772597228086-1f93b8da792d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxyb2NrJTIwY29uY2VydCUyMGVsZWN0cmljJTIwZ3VpdGFyJTIwZGFya3xlbnwwfHx8fDE3NzUxODY3NTB8MA&ixlib=rb-4.1.0&q=85",
  'R&B / Soul': "https://images.unsplash.com/photo-1774016591255-10545a911cb2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxSJTI2QiUyMHNvdWwlMjBzaW5nZXIlMjBkYXJrJTIwYWVzdGhldGljfGVufDB8fHx8MTc3NTE4Njc1MHww&ixlib=rb-4.1.0&q=85",
  'Electronic / Dance (EDM)': "https://images.unsplash.com/photo-1701705319364-ec5468034cf6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxlbGVjdHJvbmljJTIwREolMjB0dXJudGFibGUlMjBkYXJrJTIwY2x1YnxlbnwwfHx8fDE3NzUxODY3NjN8MA&ixlib=rb-4.1.0&q=85",
  'Pop': "https://images.unsplash.com/photo-1526218626217-dc65a29bb444?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwb3AlMjBtdXNpYyUyMHNpbmdlciUyMHBlcmZvcm1hbmNlJTIwY29sb3JmdWwlMjBsaWdodHN8ZW58MHx8fHwxNzc1MTg2Nzc0fDA&ixlib=rb-4.1.0&q=85",
  'Latin': "https://images.pexels.com/photos/13543306/pexels-photo-13543306.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  'Country': "https://images.pexels.com/photos/164729/pexels-photo-164729.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  'Indie / Alternative': "https://images.pexels.com/photos/9454717/pexels-photo-9454717.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  'K-Pop': "https://images.unsplash.com/photo-1526218626217-dc65a29bb444?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwb3AlMjBtdXNpYyUyMHNpbmdlciUyMHBlcmZvcm1hbmNlJTIwY29sb3JmdWwlMjBsaWdodHN8ZW58MHx8fHwxNzc1MTg2Nzc0fDA&ixlib=rb-4.1.0&q=85",
  'Afrobeats': "https://images.pexels.com/photos/13543306/pexels-photo-13543306.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  'Classical': "https://images.pexels.com/photos/359995/pexels-photo-359995.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const GENRE_GRADIENTS = {
  'Pop': 'rgba(232, 86, 127, 0.35)',
  'Hip-Hop / Rap': 'rgba(212, 162, 78, 0.35)',
  'R&B / Soul': 'rgba(139, 111, 192, 0.35)',
  'Rock': 'rgba(199, 75, 63, 0.35)',
  'Electronic / Dance (EDM)': 'rgba(59, 130, 176, 0.35)',
  'Jazz': 'rgba(212, 149, 106, 0.35)',
  'Classical': 'rgba(180, 160, 120, 0.35)',
  'Country': 'rgba(184, 146, 74, 0.35)',
  'Reggae': 'rgba(80, 180, 80, 0.35)',
  'Blues': 'rgba(70, 100, 180, 0.35)',
  'Latin': 'rgba(212, 93, 93, 0.35)',
  'Folk': 'rgba(160, 140, 100, 0.35)',
  'Metal': 'rgba(80, 80, 90, 0.35)',
  'Punk': 'rgba(220, 60, 100, 0.35)',
  'Funk': 'rgba(200, 120, 60, 0.35)',
  'Gospel / Christian': 'rgba(180, 160, 200, 0.35)',
  'Indie / Alternative': 'rgba(127, 168, 127, 0.35)',
  'K-Pop': 'rgba(255, 120, 180, 0.35)',
  'Afrobeats': 'rgba(220, 160, 80, 0.35)',
  'World Music': 'rgba(100, 180, 160, 0.35)',
  'Other': 'rgba(150, 150, 150, 0.35)',
};

const formatViews = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n;
};

const DEMO_VIDEOS_MAP = {};

export const VideoDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const passedVideo = location.state?.video || null;
  const { axiosInstance, user, token } = useAuth();
  const requireAuthForLike = useLikeAuthGate();
  const { playVideo, currentVideo, isPlaying, togglePlay } = usePlayer();

  const [video, setVideo] = useState(passedVideo || null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loading, setLoading] = useState(!passedVideo);
  const [liked, setLiked] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const fetchVideo = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/videos/${id}`);
      // Film share links land on /video/{id}; send them to the film page.
      if (res.data?.content_type === 'film') {
        navigate(`/films/${id}`, { replace: true });
        return;
      }
      setVideo(res.data);
    } catch (err) {
      // Use passed state, or look up demo, or generic fallback
      const demoVideo = DEMO_VIDEOS_MAP[id];
      setVideo(passedVideo || demoVideo || {
        id, title: 'AI Music Video', artist: 'AI Creator', genre: 'Pop',
        views: 0, likes: 0, username: 'Creator',
        created_at: new Date().toISOString(), description: ''
      });
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, id, passedVideo, navigate]);

  const fetchRelated = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/videos', { params: { limit: 6 } });
      setRelatedVideos(res.data.filter(v => v.id !== id).slice(0, 5));
    } catch (err) {
      setRelatedVideos([]);
    }
  }, [axiosInstance, id]);

  useEffect(() => {
    if (!passedVideo) fetchVideo();
    else setLoading(false);
    fetchRelated();
  }, [id, passedVideo, fetchVideo, fetchRelated]);

  // Inject per-video SEO (VideoObject + BreadcrumbList JSON-LD, OG, Twitter, canonical).
  useEffect(() => {
    if (!video || !video.id) return undefined;
    // OG / Twitter image MUST match what cards show — go through the shared
    // helper so the creator's custom_thumbnail_url is picked first. If the
    // helper falls through to the AUDIVUE data: placeholder, swap in a genre
    // hero photo for social crawlers (they don't fetch data: URLs).
    let thumbUrl = resolveThumbnailUrl(video);
    if (typeof thumbUrl === 'string' && thumbUrl.startsWith('data:')) {
      thumbUrl = GENRE_THUMBS[video.genre] || GENRE_THUMBS['Pop'];
    }
    const videoUrl = resolvePlaybackUrl(video) || (video.video_path ? `${API}/api/files/${video.video_path}` : undefined);
    const pageUrl = `https://audivue.io/video/${video.id}`;
    return applyVideoSeo({ video, videoUrl, thumbnailUrl: thumbUrl, pageUrl });
  }, [video]);

  // Auto-start full playback when arriving from Pulse (?autoplay=1)
  useEffect(() => {
    if (!video || !video.id) return;
    const params = new URLSearchParams(location.search);
    if (params.get('autoplay') === '1' && currentVideo?.id !== video.id) {
      playVideo(video, relatedVideos.length > 0 ? [video, ...relatedVideos] : [video], 0);
    }
    // playVideo is stable from context; currentVideo intentionally excluded so
    // we only re-evaluate when the route or video changes, not on every play
    // state tick. relatedVideos included so the queue picks up siblings once
    // /videos finishes loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video, location.search, relatedVideos]);

  const getThumbnail = (v) => {
    // Single source of truth: custom > Bunny > legacy → AUDIVUE branded placeholder.
    return resolveThumbnailUrl(v);
  };

  const handlePlay = () => {
    if (currentVideo?.id === video.id) {
      togglePlay();
    } else {
      playVideo(video, relatedVideos.length > 0 ? [video, ...relatedVideos] : [video], 0);
    }
  };

  const handleLike = async () => {
    // Single shared gate — bail BEFORE flipping any state so a logged-out
    // user never sees a half-completed like that vanishes on re-render.
    if (!requireAuthForLike(user, id)) return;
    try {
      await axiosInstance.post(`/videos/${id}/like`);
      setLiked(!liked);
      toast.success(liked ? 'Removed from favorites' : 'Added to favorites');
    } catch (err) {
      setLiked(!liked);
    }
  };

  const isCurrentlyPlaying = currentVideo?.id === video?.id && isPlaying;
  const genreGradient = GENRE_GRADIENTS[video?.genre] || GENRE_GRADIENTS['Pop'];

  if (loading) {
    return (
      <div className="pb-32">
        <div className="h-80 bg-zinc-900 animate-pulse" />
        <div className="px-6 pt-6 space-y-4">
          <div className="h-8 bg-zinc-800 rounded animate-pulse w-2/3" />
          <div className="h-5 bg-zinc-800 rounded animate-pulse w-1/3" />
        </div>
      </div>
    );
  }

  if (!video) return null;

  return (
    <div className="pb-32">
      {/* Hero — genre-colored gradient with large artwork */}
      <div className="relative">
        <div className="absolute inset-0 h-[340px] sm:h-[420px]" style={{
          background: `linear-gradient(180deg, ${genreGradient} 0%, rgba(5,5,5,1) 100%)`
        }} />

        <div className="relative z-10 px-4 pt-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
        </div>

        <div className="relative z-10 flex justify-center px-6 pt-4 pb-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative w-64 h-64 sm:w-80 sm:h-80 rounded-xl overflow-hidden shadow-2xl shadow-black/50"
          >
            <img
              src={getThumbnail(video)}
              alt={video.title}
              className="w-full h-full object-cover"
              data-testid="video-artwork"
            />
            {/* "Made with" badge overlay — bottom-right corner of player UI (NOT baked into video).
                Same pattern as YouTube's CC/HD pills: AUDIVUE chrome that sits in our DOM. */}
            <div className="absolute bottom-1.5 right-1.5 z-10 pointer-events-none">
              <div className="pointer-events-auto">
                <AIDisclosureBadges
                  disclosure={video.ai_disclosure}
                  onTap={(toolKey) => navigate(`/tools/${toolKey}`)}
                  dense
                  className="bg-black/60 backdrop-blur-sm rounded-md px-1.5 py-0.5 inline-flex shadow-md"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Video Info */}
      <div className="px-6 pt-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-white" data-testid="video-title">
          {video.title}
        </h1>

        {/* Artist link — prominent, always tappable */}
        <button
          className="flex items-center gap-2.5 mt-3 group"
          onClick={() => {
            if (video.user_id) navigate(`/creator/${video.user_id}`);
            else if (video.artist_id) navigate(`/creator/${video.artist_id}`);
            else navigate(`/creator/${encodeURIComponent(video.artist || video.username || 'unknown')}`);
          }}
          data-testid="artist-link"
        >
          <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
            <span className="text-xs font-bold text-white">{(video.artist || video.username || '?').charAt(0).toUpperCase()}</span>
          </div>
          <span className="text-sm font-semibold text-white group-hover:underline">
            {video.artist || video.username}
          </span>
          {video.creator_tier && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
              style={{ background: `${video.creator_tier.color}20`, color: video.creator_tier.color }}>
              {video.creator_tier.badge} {video.creator_tier.name}
            </span>
          )}
          <FoundingCreatorBadge number={video.creator_tier?.founding_number} />
          {video.creator_tier?.is_founder && <FounderBadge size="sm" />}
          <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-white transition-colors" />
        </button>

        <p className="text-xs text-zinc-500 mt-2">
          {video.genre} &bull; {formatViews(video.views || 0)} views &bull; {formatViews(video.likes || 0)} likes
        </p>

        {video.description && (
          <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{video.description}</p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="px-6 mt-5 flex items-center gap-3">
        <button
          onClick={handleLike}
          className={`p-2.5 rounded-full border transition-colors ${
            liked ? 'border-green-500 text-green-400' : 'border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500'
          }`}
          data-testid="like-btn"
        >
          <Heart className={`w-5 h-5 ${liked ? 'fill-green-400' : ''}`} />
        </button>

        <button
          className="p-2.5 rounded-full border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors"
          data-testid="add-btn"
        >
          <Plus className="w-5 h-5" />
        </button>

        <button
          onClick={() => { if (video) shareVideo(video); }}
          className="p-2.5 rounded-full border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors"
          data-testid="share-btn"
        >
          <Share2 className="w-5 h-5" />
        </button>

        <button
          onClick={() => setShowReportModal(true)}
          className="p-2.5 rounded-full border border-zinc-700 text-yellow-500 hover:text-yellow-400 hover:border-yellow-500 transition-colors"
          data-testid="report-btn"
          title="Report video"
        >
          <Flag className="w-5 h-5" />
        </button>

        <div className="flex-1" />

        <button className="p-2.5 text-zinc-400 hover:text-white transition-colors" data-testid="shuffle-btn">
          <Shuffle className="w-5 h-5" />
        </button>

        <button
          onClick={handlePlay}
          className="w-14 h-14 rounded-full bg-white hover:bg-zinc-200 flex items-center justify-center shadow-lg hover:scale-105 transition-all"
          data-testid="play-btn"
        >
          {isCurrentlyPlaying ? (
            <Pause className="w-7 h-7 text-black" />
          ) : (
            <Play className="w-7 h-7 text-black fill-black ml-1" />
          )}
        </button>
      </div>

      {/* Track row */}
      <div className="px-6 mt-8">
        <button
          onClick={handlePlay}
          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-zinc-800/50 transition-colors group"
          data-testid="track-row"
        >
          <span className={`text-sm w-5 text-right ${isCurrentlyPlaying ? 'text-white' : 'text-zinc-600'}`}>
            {isCurrentlyPlaying ? '||' : '1'}
          </span>
          <div className="w-11 h-11 rounded overflow-hidden flex-shrink-0">
            <img src={getThumbnail(video)} alt={video.title} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <p className={`text-sm font-medium truncate ${isCurrentlyPlaying ? 'text-white' : 'text-white'}`}>
              {video.title}
            </p>
            <p className="text-xs text-zinc-500 truncate">{video.artist || video.username}</p>
          </div>
          <MoreHorizontal className="w-5 h-5 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>

        <p className="text-xs text-zinc-600 mt-2 px-3">1 video &bull; {video.genre}</p>
      </div>

      {/* Comments */}
      <div className="px-6">
        <CommentSection videoId={video.id} />
      </div>

      {/* Related */}
      {relatedVideos.length > 0 && (
        <div className="px-6 mt-10">
          <h2 className="text-lg font-bold text-white mb-4">You might also like</h2>
          <div className="space-y-1">
            {relatedVideos.map((rv, idx) => (
              <button
                key={rv.id}
                onClick={() => navigate(`/video/${rv.id}`)}
                className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-zinc-800/50 transition-colors group"
                data-testid={`related-${rv.id}`}
              >
                <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
                  <img src={getThumbnail(rv)} alt={rv.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-white truncate">{rv.title}</p>
                  <p className="text-xs text-zinc-500 truncate">{rv.artist || rv.username}</p>
                </div>
                <span className="text-xs text-zinc-600">{formatViews(rv.views || 0)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        video={video}
      />
    </div>
  );
};
